import { isDefined, isNotDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { cashCommercialSpotMarginUpdated } from '@/store/cash/slice.ts';
import { type EspHeartbeat, type EspPrice, espPriceReceived, espStreamTimeout } from '@/store/esp/slice.ts';
import type { AppDispatch, AppState, GetAppState } from '@/store/store.ts';

export function startEspStreamMonitor(streamKey: string, dispatch: AppDispatch) {
  getSignalRConnector().onHeartbeatTimeout(streamKey, (streamId) => {
    if (streamKey !== streamId) {
      return;
    }
    dispatch(espStreamTimeout({ streamId }));
  });
}

export function buildReceivedEspQuoteReply(streamKey: string, getState: GetAppState, dispatch: AppDispatch) {
  return (espPrice: EspPrice | EspHeartbeat) => {
    if (espPrice.priceType === 'ESP.HEARTBEAT') {
      // it is not a price, just a heartbeat to keep the stream alive
      // so we don't need to do anything else
      return;
    }

    const midPrice = espPrice.midPrice;
    const precision = espPrice.precision;

    if (isNotDefined(midPrice) || isNotDefined(precision)) {
      // bad price
      return;
    }

    if (espPrice.streamId !== streamKey) {
      return;
    }

    const state = getState();

    const esp = state.esp.streams[streamKey];

    if (isNotDefined(esp)) {
      return;
    }

    for (const tileId of esp.subscribedTileIds) {
      initCommercialMarginSpotPoints(tileId, state, espPrice, dispatch);
    }

    dispatch(
      espPriceReceived({
        ...espPrice,

        // the frontTimestamp has to be the timestamp of when the price is received by the worker,
        // and not the timestamp of when the price is available in the front store
        // it is used to calculate the latency of the price, and to know if the price is stale or not
        // when the user executes a trade
        frontTimestamp: Number(espPrice.frontTimestamp),
        backtimestamp: Number(espPrice.backtimestamp),
      }),
    );
  };
}

function initCommercialMarginSpotPoints(tileId: string, state: AppState, reply: EspPrice, dispatch: AppDispatch) {
  const tile = state.cash[tileId];

  if (isNotDefined(tile) || tile.type !== 'FxSpot' || tile.properties.margin?.isTieringSuccess === false) {
    return;
  }

  const isMarginsInitiated = isDefined(tile.properties.margin?.commercialMarginSpotPoints);

  // initiate the margin which can be updated later by the user
  if (!isMarginsInitiated && reply.priceType === 'ESP.TRADER.PRICE') {
    const firstLadder = reply.ladders[0];

    if (isNotDefined(firstLadder)) {
      // TODO: log only once (not for all replies)
      // logger.logError("[initCommercialMarginSpotPoints] can't update commercial margin because no ladder found");
      // TODO: show toaster (only once) ?
      return;
    }

    dispatch(
      cashCommercialSpotMarginUpdated({
        tileId,
        bid: firstLadder.marginBid,
        ask: firstLadder.marginAsk,
      }),
    );
  }
}
